import { Col, Progress, Typography } from "antd";
import { PropsWithChildren } from "react";
import Loader from "./Loader";

export interface LoadingGateProps {
  readonly loading?: boolean;
  // When provided, shows an N-of-M progress bar; otherwise the gate is an indeterminate spinner.
  readonly progress?: { readonly loaded: number; readonly total: number };
  readonly label?: string;
}

// Uniform "loading gate": while `loading`, covers its area with a centered spinner (+ optional label / progress);
// otherwise renders its children. Reused by InModal and View so every edit surface shows a consistent loading phase
// before the form/content is ready (instead of an empty flash while the smart client fans out its requests).
function LoadingGate(props: PropsWithChildren<LoadingGateProps>) {
  if (!props.loading) return <>{props.children}</>;

  const hasProgress = !!props.progress && props.progress.total > 0;
  const percent = hasProgress
    ? Math.round((props.progress!.loaded / props.progress!.total) * 100)
    : undefined;

  return (
    <Col className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
      <Loader />
      {props.label && (
        <Typography.Text type="secondary">{props.label}</Typography.Text>
      )}
      {hasProgress && (
        <Progress
          className="max-w-xs"
          percent={percent}
          size="small"
          status="active"
          format={() => `${props.progress!.loaded} / ${props.progress!.total}`}
        />
      )}
    </Col>
  );
}

export default LoadingGate;
